import { DealerLayout } from "@/components/shared/DealerLayout";
import { PDFFormFiller } from "@/components/shared/PDFFormFiller";
import { uploadPreApprovedDocument } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${API}/api/templates/merchant_agreement`;

// Precise positions from pymupdf text extraction
// PDF page size: 612 x 792. Coords: [x1, y1, x2, y2] bottom-left origin
// Convert pymupdf→PDF: pdf_y = 792 - pymupdf_y

const today = new Date().toISOString().split("T")[0];

const FIELDS = [
  // === PAGE 2 (0-indexed 1) ===
  // Issue 2: Text box before ("Merchant") on line "_________________________ ("Merchant")"
  // Issue 1: Match text box widths to underscore lines on the page
  // Merchant name underscores: x=47.0-183.9, line y=139.5-150.5 → PDF y=641-653
  { id: "merchant_name_p2", page: 1, type: "text", rect: [47, 641, 184, 653], label: "Merchant Name" },
  // Date underscores: x=460.3-520.5, same line
  { id: "effective_date_p2", page: 1, type: "text", rect: [460, 641, 521, 653], label: "Effective Date" },

  // === PAGE 9 (0-indexed 8) ===
  // Issue 4: Business Name text box (not signature)
  // "Business Name:" at [280,616,365,628], field after label
  { id: "business_name_p9", page: 8, type: "text", rect: [370, 164, 555, 176], label: "Business Name" },
  // Issue 5: Owner Name text box after "Owner Name:" [280,637,365,649]
  { id: "owner_name_p9", page: 8, type: "text", rect: [370, 143, 555, 155], label: "Owner Name" },
  // Owner Title text box after "Owner Title:" [280,667,346,679]
  { id: "owner_title_p9", page: 8, type: "text", rect: [350, 113, 555, 125], label: "Owner Title" },
  // Signature box above "(OWNER's SIGNATURE)" [369,710,482,722]
  // "By:" at [280,698,301,710], "X" at [334,698]
  { id: "owner_sig_p9", page: 8, type: "signature", rect: [310, 72, 555, 94], label: "Owner Signature" },
  // Issue 6: Date text box after "Date:" [280,727,312,739]
  { id: "date_p9", page: 8, type: "text", rect: [315, 53, 555, 65], label: "Date" },

  // === PAGE 11 (0-indexed 10) ===
  // Issue 7: "Legal Business Name:" at [30,146,123,156]
  { id: "legal_business_name", page: 10, type: "text", rect: [125, 636, 270, 646], label: "Legal Business Name" },
  // "Doing Business As:" at [273,146,356,156]
  { id: "dba", page: 10, type: "text", rect: [358, 636, 555, 646], label: "DBA" },
  // Issue 2: EIN - match widget rect [124.0, 163.2, 272.5, 181.7] → PDF [124, 610, 273, 629]
  { id: "ein", page: 10, type: "text", rect: [124, 610, 273, 629], label: "EIN" },
  // "Contact Full Name:" at [273,172,355,182]
  { id: "contact_name", page: 10, type: "text", rect: [358, 610, 555, 620], label: "Contact Full Name" },
  // "Business Street Address:" at [30,194,135,204]
  { id: "business_street", page: 10, type: "text", rect: [138, 588, 270, 598], label: "Street Address" },
  // "City:" at [273,194,298,204]
  { id: "city", page: 10, type: "text", rect: [300, 588, 378, 598], label: "City" },
  // "State:" at [381,194,410,204]
  { id: "state", page: 10, type: "text", rect: [412, 588, 473, 598], label: "State" },
  // "ZIP:" at [476,194,495,204]
  { id: "zip", page: 10, type: "text", rect: [497, 588, 555, 598], label: "ZIP" },
  // "Email Address:" at [30,217,100,227]
  { id: "email", page: 10, type: "text", rect: [102, 565, 270, 575], label: "Email" },
  // "Phone:" at [273,217,311,227]
  { id: "phone", page: 10, type: "text", rect: [313, 565, 378, 575], label: "Phone" },
  // "Fax:" at [381,217,403,227]
  { id: "fax", page: 10, type: "text", rect: [405, 565, 473, 575], label: "Fax" },
  // Guarantor section
  // "First and Last Name:" at [30,271,116,281]
  { id: "guarantor_name", page: 10, type: "text", rect: [118, 511, 258, 521], label: "Name" },
  // Issue 2: SSN - match widget rect [285.6, 262.2, 434.5, 280.7] → PDF [286, 511, 435, 530]
  { id: "ssn", page: 10, type: "text", rect: [286, 511, 435, 530], label: "SSN" },
  // "DOB:" at [440,271,464,281]
  { id: "dob", page: 10, type: "text", rect: [466, 511, 555, 521], label: "DOB" },
  // "Home Street Address:" at [30,298,121,308]
  { id: "home_street", page: 10, type: "text", rect: [123, 484, 258, 494], label: "Home Street" },
  // "City:" at [262,298,285,308]
  { id: "home_city", page: 10, type: "text", rect: [287, 484, 363, 494], label: "City" },
  // "State:" at [366,298,395,308]
  { id: "home_state", page: 10, type: "text", rect: [397, 484, 469, 494], label: "State" },
  // "ZIP:" at [472,298,491,308]
  { id: "home_zip", page: 10, type: "text", rect: [493, 484, 555, 494], label: "ZIP" },
  // Issue 9: "Authorized Signature: X" at [30,330,131,340]
  { id: "auth_sig", page: 10, type: "signature", rect: [135, 452, 363, 462], label: "Authorized Signature" },
  // Issue 10: "Date:" at [366,330,393,340]
  { id: "auth_date", page: 10, type: "text", rect: [395, 452, 555, 462], label: "Date" },
  // Banking section
  // "Business name on bank account:" at [30,394,165,404]
  { id: "bank_name", page: 10, type: "text", rect: [168, 388, 346, 398], label: "Bank Name" },
  // "Bank Account Number:" at [349,394,446,404]
  { id: "bank_acct", page: 10, type: "text", rect: [448, 388, 555, 398], label: "Account #" },
  // "Business full address for the bank account:" at [30,415,204,425]
  { id: "bank_addr_1", page: 10, type: "text", rect: [206, 367, 346, 377], label: "Bank Address" },
  // "Bank Routing Number:" at [349,415,445,425]
  { id: "bank_routing", page: 10, type: "text", rect: [447, 367, 555, 377], label: "Routing #" },
  // Second bank address line at [30,434] area
  { id: "bank_addr_2", page: 10, type: "text", rect: [30, 348, 346, 358], label: "Bank Address 2" },
  // "Invoice Delivery Email:" at [349,434,450,444]
  { id: "invoice_email", page: 10, type: "text", rect: [452, 348, 555, 358], label: "Invoice Email" },
  // "INFORMATION:" field at [30,465]
  { id: "information", page: 10, type: "text", rect: [100, 317, 555, 327], label: "Information" },

  // === PAGE 12 (0-indexed 11) - SUBSTITUTE W-9 ===
  // Issue 11: "Legal Business Name*:" at [45,146,141,156]
  { id: "w9_legal_name", page: 11, type: "text", rect: [145, 636, 555, 646], label: "Legal Business Name" },
  // Issue 12: Business type checkboxes - exact pymupdf positions
  // Checkbox rects from pymupdf: 9x9 boxes at exact positions
  { id: "cb_individual", page: 11, type: "checkbox", rect: [57, 543, 66, 552], label: "Individual" },
  { id: "cb_c_corp", page: 11, type: "checkbox", rect: [190, 543, 199, 552], label: "C Corp" },
  { id: "cb_s_corp", page: 11, type: "checkbox", rect: [292, 543, 301, 552], label: "S Corp" },
  { id: "cb_trust", page: 11, type: "checkbox", rect: [379, 543, 388, 552], label: "Trust" },
  { id: "cb_partnership", page: 11, type: "checkbox", rect: [57, 521, 66, 530], label: "Partnership" },
  { id: "cb_llc", page: 11, type: "checkbox", rect: [190, 521, 199, 530], label: "LLC" },
  // LLC sub-types
  { id: "cb_llc_c", page: 11, type: "checkbox", rect: [231, 503, 240, 512], label: "LLC C Corp" },
  { id: "cb_llc_s", page: 11, type: "checkbox", rect: [313, 503, 322, 512], label: "LLC S Corp" },
  { id: "cb_llc_p", page: 11, type: "checkbox", rect: [395, 503, 405, 512], label: "LLC Partnership" },
  // Issue 13: TIN type checkboxes - exact pymupdf positions
  { id: "cb_ssn", page: 11, type: "checkbox", rect: [51, 351, 60, 360], label: "SSN" },
  { id: "cb_ein", page: 11, type: "checkbox", rect: [142, 351, 151, 360], label: "EIN" },
  // Issue 14: TIN - one long text box covering all 9 digit squares
  // Widget EIN at pymupdf [52.56, 461.52, 205.56, 481.80] → PDF [53, 310, 206, 331]
  { id: "tin", page: 11, type: "text", rect: [53, 310, 206, 331], label: "TIN" },
  // W-9 Business Address section
  // "Legal Business Address (No Po Box):" at [45,347,198,357]
  { id: "w9_address", page: 11, type: "text", rect: [200, 435, 555, 445], label: "Address" },
  // "City:" at [45,370,66,380]
  { id: "w9_city", page: 11, type: "text", rect: [68, 412, 202, 422], label: "City" },
  // "State:" at [205,370,231,380]
  { id: "w9_state", page: 11, type: "text", rect: [233, 412, 299, 422], label: "State" },
  // "ZIP Code:" at [302,370,346,380]
  { id: "w9_zip", page: 11, type: "text", rect: [348, 412, 555, 422], label: "ZIP" },
  // Issue 15: "Signature:" at [192,705,241,715]
  { id: "w9_sig", page: 11, type: "signature", rect: [245, 77, 328, 87], label: "Signature" },
  // "Title:" at [332,705,361,715]
  { id: "w9_title", page: 11, type: "text", rect: [363, 77, 458, 87], label: "Title" },
  // Issue 16: "Date: _________" at [462,705,534,715]
  { id: "w9_date", page: 11, type: "text", rect: [500, 77, 555, 87], label: "Date" },
];

// Map field IDs to actual PDF AcroForm field names for pdf-lib
const PDF_FIELD_MAPPING = {
  business_name_p9: "Owner Name",
  owner_name_p9: "Owner Name",
  owner_title_p9: "Owner Title",
  dba: "Doing Business As",
  contact_name: "Contact Full Name",
  business_street: "Business Street Address",
  city: "City",
  state: "State",
  zip: "ZIP",
  email: "Email Address",
  phone: "Phone",
  fax: "Fax",
  guarantor_name: "First and Last Name",
  ssn: "SSN",
  dob: "DOB",
  home_street: "Home Street Address",
  home_city: "Home City",
  home_state: "Home State",
  home_zip: "Home ZIP Code",
  bank_name: "Business name on bank account",
  bank_acct: "Bank Account Number",
  bank_addr_1: "Business full address for the bank account 1",
  bank_addr_2: "Business full address for the bank account 2",
  bank_routing: "Bank Routing Number",
  invoice_email: "Invoice Delivery Email",
  information: "INFORMATION",
  w9_address: "Legal Business Address No Po Box",
  w9_city: "City_3",
  w9_state: "State_3",
  w9_zip: "ZIP Code",
};

// Manual text for fields that aren't in the AcroForm
const MANUAL_TEXT_MAPPING = [
  { fieldId: "merchant_name_p2", page: 1, x: 49, y: 645, fontSize: 10 },
  { fieldId: "effective_date_p2", page: 1, x: 422, y: 645, fontSize: 10 },
  { fieldId: "legal_business_name", page: 10, x: 127, y: 639, fontSize: 9 },
  { fieldId: "ein", page: 10, x: 122, y: 613, fontSize: 9 },
  { fieldId: "auth_date", page: 10, x: 397, y: 455, fontSize: 9 },
  { fieldId: "date_p9", page: 8, x: 317, y: 56, fontSize: 9 },
  { fieldId: "w9_legal_name", page: 11, x: 147, y: 639, fontSize: 9 },
  { fieldId: "tin", page: 11, x: 55, y: 312, fontSize: 10 },
  { fieldId: "w9_title", page: 11, x: 365, y: 80, fontSize: 9 },
  { fieldId: "w9_date", page: 11, x: 502, y: 80, fontSize: 9 },
];

export default function MerchantAgreementGenerator() {
  const navigate = useNavigate();

  return (
    <DealerLayout>
      <PDFFormFiller
        pdfUrl={PDF_URL}
        fields={FIELDS}
        title="Merchant Agreement"
        subtitle="VidaPay Account Setup Form - Fill in the highlighted fields directly on the document."
        docType="merchant_agreement"
        onSave={(file, docType) => uploadPreApprovedDocument(file, docType).then(() => navigate("/dealer/documents"))}
        onBack={() => navigate("/dealer/documents")}
        pdfFieldMapping={PDF_FIELD_MAPPING}
        manualTextMapping={MANUAL_TEXT_MAPPING}
      />
    </DealerLayout>
  );
}
